import React, { memo, useMemo } from 'react';
import {
  Box,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { SPACING } from '../../theme';
import PropTypes from 'prop-types';

/**
 * Shared pagination component used across templates, executions, experiments,
 * groups, and users pages.
 *
 * Expects 1-based currentPage and totalPages.
 */
const PaginationBar = ({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPrev,
  onNext,
  onPageSizeChange,
  onPageClick,
}) => {
  const theme = useTheme();

  // Use onPageClick if provided, otherwise fallback to onPrev/onNext
  const handlePageClick =
    onPageClick ||
    (page => {
      if (page < currentPage) onPrev();
      else if (page > currentPage) onNext();
    });

  // Calculate which page numbers to show
  const pageNumbers = useMemo(() => {
    if (!totalPages || totalPages <= 1) {
      return [];
    }
    const pages = [];

    if (totalPages <= 7) {
      // Show all pages if total is small (no ellipsis needed)
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate visible range around current page
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      // If near the beginning, show pages 2-5
      if (currentPage <= 3) {
        start = 2;
        end = Math.min(5, totalPages - 1);
      }
      // If near the end, show last 4 pages before the last page
      else if (currentPage >= totalPages - 2) {
        start = Math.max(2, totalPages - 4);
        end = totalPages - 1;
      }
      // Otherwise show current page ± 1
      else {
        start = currentPage - 1;
        end = currentPage + 1;
      }

      // Add ellipsis before range if there's a gap
      if (start > 2) {
        pages.push('ellipsis-start');
      }

      // Add visible page range
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add ellipsis after range if there's a gap
      if (end < totalPages - 1) {
        pages.push('ellipsis-end');
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  }, [currentPage, totalPages]);

  // Early return after all hooks are called
  if (!totalPages || totalPages <= 1) return null;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: SPACING.xxl,
          mb: SPACING.lg,
          gap: 1,
        }}
      >
        {/* Previous Arrow */}
        <IconButton
          onClick={onPrev}
          disabled={currentPage <= 1}
          sx={{
            color:
              currentPage <= 1
                ? theme.palette.text.disabled
                : theme.palette.text.secondary,
            '&:hover': {
              backgroundColor:
                currentPage <= 1 ? 'transparent' : theme.palette.action.hover,
            },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>

        {/* Page Numbers */}
        {pageNumbers.map((page, index) => {
          if (page === 'ellipsis-start' || page === 'ellipsis-end') {
            return (
              <Typography
                key={`ellipsis-${index}`}
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  px: 1,
                }}
              >
                ...
              </Typography>
            );
          }

          const isSelected = page === currentPage;

          return (
            <Box
              key={page}
              onClick={() => handlePageClick(page)}
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                backgroundColor: isSelected
                  ? theme.palette.primary.main
                  : 'transparent',
                border: isSelected
                  ? `1px solid ${
                      theme.palette.primary.contrastText || '#FFFFFF'
                    }`
                  : 'none',
                color: isSelected
                  ? theme.palette.primary.contrastText || '#FFFFFF'
                  : theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: isSelected
                    ? theme.palette.primary.dark
                    : theme.palette.action.hover,
                },
                transition: 'all 0.2s ease',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isSelected ? 600 : 400,
                  fontSize: '0.875rem',
                }}
              >
                {page}
              </Typography>
            </Box>
          );
        })}

        {/* Next Arrow */}
        <IconButton
          onClick={onNext}
          disabled={currentPage >= totalPages}
          sx={{
            color:
              currentPage >= totalPages
                ? theme.palette.text.disabled
                : theme.palette.text.primary,
            '&:hover': {
              backgroundColor:
                currentPage >= totalPages
                  ? 'transparent'
                  : theme.palette.action.hover,
            },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: SPACING.lg }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Page Size</InputLabel>
          <Select
            value={pageSize}
            onChange={e => onPageSizeChange(e.target.value)}
            label="Page Size"
          >
            <MenuItem value={10}>10 per page</MenuItem>
            <MenuItem value={25}>25 per page</MenuItem>
            <MenuItem value={50}>50 per page</MenuItem>
            <MenuItem value={100}>100 per page</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </>
  );
};

PaginationBar.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number,
  totalCount: PropTypes.number,
  pageSize: PropTypes.number.isRequired,
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  onPageClick: PropTypes.func, // Optional: for direct page navigation
};

export default memo(PaginationBar);
